import { useState, useEffect } from "react";
import { createSearchParams } from "react-router-dom";
import {
  fetchProtocols,
  deleteProtocol,
  toggleArchiveProtocol,
  fetchTemplates,
  deleteTemplate,
} from "./protocolsService";
import { ProtocolsHeader } from "./ProtocolsHeader";
import { ProtocolsColumns } from "./table-config";
import { Loader } from "../../shared/components/Loader";
import ProtocolDrawer from "../../shared/layout/ProtocolDrawer";
import { useAppState } from "../../lib/appContext/AppContext";
import { useTranslation } from "react-i18next";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { isWorkspaceOwnerOperationAllowed } from "./permissions";
import CreateProtocolModal, {
  type CreateProtocolOption,
} from "../../shared/components/CreateProtocolModal";
import "./style.less";
import ProtocolTabs from "./ProtocolTabs";
import { useProtocolTemplatesTabEnabled } from "./useProtocolTemplatesTabEnabled";
import SaveTemplateModal from "../create-protocol/shared/components/SaveTemplateModal";
import { useProtocolsRouter } from "../../shared/custom-hooks/useProtocolsRouter";
import { useSyncPermissions } from "../../shared/custom-hooks/useSyncPermissions";
import { useTrackingEvent } from "../../shared/custom-hooks/useTracking";
import {
  ProtocolAmplitudeEvent,
  ProtocolTemplatesAmplitudeEvent,
} from "../../shared/utils/amplitudeEvents";
import { useFeatureFlags } from "../../context/LDFlagsContext";

export const getUpdatedPaginationForFilterChange = (
  pagination: { current: number; pageSize: number; total: number },
  key: string
): { current: number; pageSize: number; total: number } => {
  if (key !== "tab") {
    return pagination;
  }

  return {
    ...pagination,
    current: 1,
  };
};

export const Protocols = (): JSX.Element => {
  const { t } = useTranslation();
  const { router: protocolsRouter, workspaceId, orgId } = useProtocolsRouter();
  const { stateValue } = useAppState();
  const [protocols, setProtocols] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateProtocolModalOpen, setIsCreateProtocolModalOpen] =
    useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedProtocolId, setSelectedProtocolId] = useState<string>("");
  const [selectedProtocolName, setSelectedProtocolName] = useState<string>("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    searchText: "",
    status: "",
    tab: "active",
  });

  const protocolExtraheaders = {
    "org-id": orgId ?? "",
    "workspace-id": workspaceId ?? "",
  };

  const { data: permissions } = useSyncPermissions({ workspaceId, orgId });

  const allowed = permissions?.allowed_operations ?? [];
  const isWorkspaceOwner = isWorkspaceOwnerOperationAllowed(allowed);
  const isTemplatesTabEnabled = useProtocolTemplatesTabEnabled({
    orgId: orgId ?? "",
    workspaceId: workspaceId ?? "",
    isWorkspace: isWorkspaceOwner,
  });

  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const { enableProtocolTemplates } = useFeatureFlags();

  const trackProtocolEvents = useTrackingEvent({
    page: ProtocolAmplitudeEvent.PROTOCOLS_PAGE,
    group: ProtocolAmplitudeEvent.PROTOCOLS_GROUP,
    module: ProtocolAmplitudeEvent.PROTOCOLS_GROUP,
  });

  useEffect((): void => {
    setIsLoading(true);
    void fetchData(pagination, filters);
  }, [stateValue.orgValue, permissions, filters, isTemplatesTabEnabled]);

  useEffect((): void => {
    if (!isTemplatesTabEnabled && filters.tab === "templates") {
      setFilters((currentFilters) => ({
        ...currentFilters,
        tab: "active",
      }));
    }
  }, [filters.tab, isTemplatesTabEnabled]);

  const fetchData = async (pagination: any, filters: any): Promise<void> => {
    try {
      if (filters.tab === "templates" && isTemplatesTabEnabled) {
        const templatesData = await fetchTemplates(
          orgId ?? "",
          pagination.current,
          pagination.pageSize,
          false,
          filters.searchText,
          filters.status,
          isWorkspaceOwner,
          workspaceId ?? ""
        );

        setProtocols(templatesData.templates);
        setPagination({ ...pagination, total: templatesData?.total });
      } else {
        if (orgId) {
          const archived = filters.tab === "archived";
          const data = await fetchProtocols(
            orgId ?? "",
            pagination.current,
            pagination.pageSize,
            archived,
            filters.searchText,
            filters.status,
            isWorkspaceOwner,
            workspaceId ?? ""
          );
          setProtocols(data?.protocols || []);
          setPagination({ ...pagination, total: data?.count || 0 });
        }
      }
    } catch (error) {
      console.error("Error fetching protocols:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleArchive = async (
    protocolId: string,
    orgId: string,
    workspaceId: string
  ): Promise<void> => {
    try {
      setIsLoading(true);
      await toggleArchiveProtocol(protocolId, true, orgId, workspaceId);
      await fetchData(pagination, filters);
    } catch (error) {
      setIsLoading(false);
      console.error("Archive failed:", error);
    }
  };

  const handleUnarchive = async (
    protocolId: string,
    orgId: string,
    workspaceId: string
  ): Promise<void> => {
    try {
      setIsLoading(true);
      await toggleArchiveProtocol(protocolId, false, orgId, workspaceId);
      await fetchData(pagination, filters);
    } catch (error) {
      setIsLoading(false);
      console.error("Unarchive failed:", error);
    }
  };

  const handleDelete = async (
    recordId: string,
    orgId: string,
    workspaceId: string
  ): Promise<void> => {
    try {
      setIsLoading(true);
      if (filters.tab === "templates") {
        await deleteTemplate(recordId, orgId, workspaceId);
      } else {
        await deleteProtocol(recordId, orgId, workspaceId);
      }
      await fetchData(pagination, filters);
    } catch (error) {
      setIsLoading(false);
      console.error("Error deleting protocol:", error);
    }
  };

  const handleViewProtocol = (protocolId: string): void => {
    setSelectedProtocolId(protocolId);
    setDrawerVisible(true);
  };

  const handleRowClick = (protocolId: string): void => {
    handleViewProtocol(protocolId);
  };

  const handleCreateProtocol = (): void => {
    if (!enableProtocolTemplates) {
      navigateToNewProtocolFlow();
      return;
    }
    void (async () => {
      const templatesAvailable = await hasTemplatesAvailable();

      if (!templatesAvailable) {
        navigateToNewProtocolFlow();
        return;
      }

      setIsCreateProtocolModalOpen(true);
    })();
  };

  const navigateToNewProtocolFlow = (): void => {
    protocolsRouter.goToProtocolsCreatePage(undefined, "trial-type");
  };

  const hasTemplatesAvailable = async (): Promise<boolean> => {
    if (!isTemplatesTabEnabled || !orgId) {
      return false;
    }

    try {
      const templatesData = await fetchTemplates(
        orgId,
        1,
        1,
        false,
        "",
        "",
        isWorkspaceOwner,
        workspaceId ?? ""
      );

      return templatesData.total > 0;
    } catch (error) {
      console.error(
        "Error fetching templates for create protocol flow:",
        error
      );

      // Keep the choice modal as a safe fallback if template availability check fails.
      return true;
    }
  };

  const handleCreateProtocolModalCancel = (): void => {
    setIsCreateProtocolModalOpen(false);
  };

  const handleCreateProtocolModalContinue = (
    selectedOption: CreateProtocolOption,
    templateId?: string,
    templateName?: string
  ): void => {
    if (selectedOption === "newProtocol") {
      navigateToNewProtocolFlow();
    } else if (selectedOption === "template" && templateId) {
      void trackProtocolEvents({
        event: ProtocolTemplatesAmplitudeEvent.USE_TEMPLATE,
        originAction: "use-template",
      });
      protocolsRouter.goToProtocolsCreatePage(
        createSearchParams({
          template_id: templateId,
          ...(templateName ? { template_name: templateName } : {}),
        }).toString(),
        "trial-type"
      );
    }

    setIsCreateProtocolModalOpen(false);
  };

  const hideDrawer = (): void => {
    setSelectedProtocolId("");
    setDrawerVisible(false);
  };

  const updateFilter = (key: string, value: string): void => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      [key]: value,
    }));
    setPagination((currentPagination) =>
      getUpdatedPaginationForFilterChange(currentPagination, key)
    );
  };

  const handlePagination = async (pagination: any): Promise<void> => {
    setPagination(pagination);
    await fetchData(pagination, filters);
  };

  const handleCloseTemplateModal = (): void => {
    setIsTemplateModalOpen(false);
  };

  const handleOpenTemplateModal = (
    protocolId: string,
    protocolName: string
  ): void => {
    setSelectedProtocolId(protocolId);
    setSelectedProtocolName(protocolName);
    setIsTemplateModalOpen(true);
  };

  const columns = ProtocolsColumns(
    (recordId: string) => {
      void handleDelete(recordId, orgId ?? "", workspaceId ?? "");
    },
    async (protocolId: string) => {
      await handleArchive(protocolId, orgId ?? "", workspaceId ?? "");
    },
    async (protocolId: string) => {
      await handleUnarchive(protocolId, orgId ?? "", workspaceId ?? "");
    },
    filters.tab,
    handleOpenTemplateModal
  );

  return (
    <div className="protocols-dashboard-page">
      <ProtocolsHeader
        title={t("protocols.title")}
        // showCreateButton={protocols.length > 0}
        showCreateButton
        onCreate={handleCreateProtocol}
      />
      <div className="body-content-protocol pt-0">
        {isLoading && <Loader mask message="" />}
        <div className="protocol-table-container">
          <ProtocolTabs
            protocols={protocols}
            columns={columns}
            pagination={pagination}
            filters={filters}
            handlePagination={handlePagination}
            handleRowClick={handleRowClick}
            updateFilter={updateFilter}
            handleUnarchive={handleUnarchive}
            permissions={permissions}
            isLoading={isLoading}
            isTemplatesTabEnabled={isTemplatesTabEnabled}
          />
        </div>
      </div>
      <ProtocolDrawer
        open={drawerVisible}
        onClose={hideDrawer}
        protocolId={selectedProtocolId}
        extraHeaders={protocolExtraheaders}
      />
      <CreateProtocolModal
        open={isCreateProtocolModalOpen}
        onCancel={handleCreateProtocolModalCancel}
        onContinue={handleCreateProtocolModalContinue}
      />
      <SaveTemplateModal
        open={isTemplateModalOpen}
        protocolId={selectedProtocolId}
        protocolName={selectedProtocolName}
        orgId={orgId ?? ""}
        workspaceId={workspaceId ?? ""}
        onClose={handleCloseTemplateModal}
      />
      <ToastContainer stacked />
    </div>
  );
};

export default Protocols;
